<template>
  <div class="faq-page">
    <b-row>
      <b-col
        lg="8"
        md="10"
        sm="12"
        class="mx-auto"
      >
        <!-- FAQ Header -->
        <div class="text-center mb-3">
          <h2>
            {{ $t('DEBTIA_FAQ_TITLE') }}
          </h2>
          <p class="text-muted">
            {{ $t('DEBTIA_FAQ_SUBTITLE') }}
          </p>
        </div>

        <!-- Search -->
        <b-card class="mb-2">
          <b-input-group>
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              v-model="searchQuery"
              :placeholder="searchPlaceholder"
            />
          </b-input-group>
        </b-card>

        <!-- FAQ Categories -->
        <div
          v-for="(category, categoryIndex) in filteredFaqData"
          :key="categoryIndex"
          class="mb-2"
        >
          <h5 class="mb-1">
            <feather-icon
              :icon="category.icon"
              size="18"
              class="mr-50 text-primary"
            />
            {{ category.title }}
          </h5>

          <b-card no-body>
            <app-collapse accordion>
              <app-collapse-item
                v-for="(item, itemIndex) in category.questions"
                :key="itemIndex"
                :title="item.question"
              >
                <div v-html="item.answer" />
              </app-collapse-item>
            </app-collapse>
          </b-card>
        </div>

        <!-- No Results -->
        <b-card
          v-if="filteredFaqData.length === 0"
          class="text-center py-3"
        >
          <feather-icon
            icon="SearchIcon"
            size="48"
            class="text-muted mb-1"
          />
          <h4 class="text-muted">
            {{ $t('DEBTIA_FAQ_NO_RESULTS') }}
          </h4>
          <p class="text-muted mb-0">
            {{ $t('DEBTIA_FAQ_TRY_DIFFERENT') }}
          </p>
        </b-card>

        <!-- Contact Card -->
        <b-card class="mt-2">
          <b-row class="align-items-center">
            <b-col
              md="8"
              sm="12"
            >
              <h4>
                {{ $t('DEBTIA_FAQ_STILL_QUESTIONS') }}
              </h4>
              <p class="text-muted mb-md-0">
                {{ $t('DEBTIA_FAQ_CONTACT_TEXT') }}
              </p>
            </b-col>
            <b-col
              md="4"
              sm="12"
              class="text-md-right"
            >
              <b-button
                variant="primary"
                href="mailto:kontakt@debtia.dk"
              >
                <feather-icon
                  icon="MailIcon"
                  class="mr-50"
                />
                {{ $t('DEBTIA_FAQ_CONTACT_US') }}
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BInputGroup,
  BInputGroupPrepend,
  BFormInput,
  BButton,
} from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BButton,
    AppCollapse,
    AppCollapseItem,
  },
  data() {
    return {
      searchQuery: '',
    }
  },
  computed: {
    searchPlaceholder() {
      return this.$t('DEBTIA_FAQ_SEARCH_PLACEHOLDER')
    },
    faqData() {
      return [
        {
          title: this.$t('DEBTIA_FAQ_CAT_ABOUT_CASE'),
          icon: 'FolderIcon',
          questions: [
            {
              question: this.$t('DEBTIA_FAQ_Q_WHAT_IS_DEBT_CASE'),
              answer: this.$t('DEBTIA_FAQ_A_WHAT_IS_DEBT_CASE'),
            },
            {
              question: this.$t('DEBTIA_FAQ_Q_WHY_LETTER'),
              answer: this.$t('DEBTIA_FAQ_A_WHY_LETTER'),
            },
            {
              question: this.$t('DEBTIA_FAQ_Q_HOW_SEE_DETAILS'),
              answer: this.$t('DEBTIA_FAQ_A_HOW_SEE_DETAILS'),
            },
          ],
        },
        {
          title: this.$t('DEBTIA_FAQ_CAT_PAYMENT'),
          icon: 'CreditCardIcon',
          questions: [
            {
              question: this.$t('DEBTIA_FAQ_Q_HOW_PAY'),
              answer: this.$t('DEBTIA_FAQ_A_HOW_PAY'),
            },
            {
              question: this.$t('DEBTIA_FAQ_Q_PAYMENT_PLAN'),
              answer: this.$t('DEBTIA_FAQ_A_PAYMENT_PLAN'),
            },
            {
              question: this.$t('DEBTIA_FAQ_Q_MISSED_PAYMENT'),
              answer: this.$t('DEBTIA_FAQ_A_MISSED_PAYMENT'),
            },
            {
              question: this.$t('DEBTIA_FAQ_Q_PAYMENT_DEADLINE'),
              answer: this.$t('DEBTIA_FAQ_A_PAYMENT_DEADLINE'),
            },
          ],
        },
        {
          title: this.$t('DEBTIA_FAQ_CAT_RIGHTS'),
          icon: 'ShieldIcon',
          questions: [
            {
              question: this.$t('DEBTIA_FAQ_Q_DEBTOR_RIGHTS'),
              answer: this.$t('DEBTIA_FAQ_A_DEBTOR_RIGHTS'),
            },
            {
              question: this.$t('DEBTIA_FAQ_Q_DISAGREE_CLAIM'),
              answer: this.$t('DEBTIA_FAQ_A_DISAGREE_CLAIM'),
            },
            {
              question: this.$t('DEBTIA_FAQ_Q_DATA_PROTECTION'),
              answer: this.$t('DEBTIA_FAQ_A_DATA_PROTECTION'),
            },
          ],
        },
        {
          title: this.$t('DEBTIA_FAQ_CAT_CONTACT'),
          icon: 'HeadphonesIcon',
          questions: [
            {
              question: this.$t('DEBTIA_FAQ_Q_HOW_CONTACT'),
              answer: this.$t('DEBTIA_FAQ_A_HOW_CONTACT'),
            },
            {
              question: this.$t('DEBTIA_FAQ_Q_REQUEST_CALLBACK'),
              answer: this.$t('DEBTIA_FAQ_A_REQUEST_CALLBACK'),
            },
            {
              question: this.$t('DEBTIA_FAQ_Q_FORGOT_PASSWORD'),
              answer: this.$t('DEBTIA_FAQ_A_FORGOT_PASSWORD'),
            },
          ],
        },
      ]
    },
    filteredFaqData() {
      if (!this.searchQuery) {
        return this.faqData
      }

      const query = this.searchQuery.toLowerCase()
      return this.faqData
        .map(category => ({
          ...category,
          questions: category.questions.filter(
            item => item.question.toLowerCase().includes(query)
              || item.answer.toLowerCase().includes(query),
          ),
        }))
        .filter(category => category.questions.length > 0)
    },
  },
}
</script>

<style lang="scss" scoped>
.faq-page {
  min-height: 400px;

  ::v-deep .collapse-title {
    font-weight: 500;
  }

  ::v-deep .card-body ul {
    margin-bottom: 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
